import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { leaveWithoutGuard } from "@/lib/navigationGuardRegistry";
import { endCurrentSession } from "@/lib/authNavigation";
import { clearDeviceTrust, sessionIdentity, trustedUntil } from "@/lib/trustedDevice";
import { isDefinitelySignedOut, type AuthErrorLike } from "@/lib/authSessionErrors";
import {
  BACKGROUND_RECHECK_MS,
  evaluateStaffSession,
  sessionStartFrom,
  type StaffSessionVerdict,
} from "@/lib/staffSessionPolicy";

const ACTIVITY_KEY = "evohome.staff.lastActivityAt";
const WRITE_THROTTLE_MS = 15_000;
const CHECK_INTERVAL_MS = 30_000;

function readActivity(userId: string): number | null {
  try {
    const raw = localStorage.getItem(`${ACTIVITY_KEY}:${userId}`);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeActivity(userId: string, at: number) {
  try {
    localStorage.setItem(`${ACTIVITY_KEY}:${userId}`, String(at));
  } catch {
    /* storage unavailable: the in-memory value still applies */
  }
}

/**
 * Locks professional/admin sessions after ~30 minutes without activity (shared
 * across tabs) or 8 hours after sign-in. Short tab switches are harmless because
 * idle time is measured from the last real interaction. Network failures while
 * re-checking are never treated as a sign-out.
 */
export default function StaffSessionGuard({
  userId,
  lastSignInAt,
  loginPath,
  sessionId,
}: {
  userId: string;
  lastSignInAt: string | null | undefined;
  loginPath: string;
  sessionId?: string | null;
}) {
  const navigate = useNavigate();
  const lastActivity = useRef<number>(Date.now());
  const lastWrite = useRef<number>(0);
  const hiddenAt = useRef<number | null>(null);
  const ending = useRef(false);

  useEffect(() => {
    ending.current = false;
    const now = Date.now();
    lastActivity.current = Math.max(readActivity(userId) ?? now, now);
    writeActivity(userId, lastActivity.current);
    const sessionStartedAt = sessionStartFrom(lastSignInAt, now);
    let verifiedTrust = false;

    const lock = async (verdict: StaffSessionVerdict) => {
      if (ending.current) return;
      ending.current = true;
      const outcome = await endCurrentSession();
      if (!outcome.ok) {
        // The server could not be reached; end the session on this device anyway
        // because the timeout (not the network error) is the reason.
        await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
      }
      const reason = verdict === "absolute_expired" ? "session_expired" : "idle";
      leaveWithoutGuard(() => navigate(`${loginPath}?reason=${reason}`, { replace: true }));
    };

    const check = () => {
      const shared = readActivity(userId);
      if (shared && shared > lastActivity.current) lastActivity.current = shared;
      if (verifiedTrust && trustedUntil(userId, sessionId ?? null)) return;
      const verdict = evaluateStaffSession({
        now: Date.now(),
        lastActivityAt: lastActivity.current,
        sessionStartedAt,
      });
      if (verdict !== "active") void lock(verdict);
    };

    const onActivity = () => {
      const t = Date.now();
      lastActivity.current = t;
      if (t - lastWrite.current > WRITE_THROTTLE_MS) {
        lastWrite.current = t;
        writeActivity(userId, t);
      }
    };

    let verifying = false;
    let disposed = false;
    const verify = async () => {
      if (verifying || ending.current) return;
      verifying = true;
      try {
        const { data, error } = await supabase.auth.getUser();
        if (disposed) return;
        if (error) { clearDeviceTrust(); verifiedTrust = false; }
        if ((!error && data?.user?.id !== userId) || (error && !data?.user && isDefinitelySignedOut(error as AuthErrorLike))) {
          clearDeviceTrust();
          await lock("absolute_expired");
        } else if (trustedUntil(userId, sessionId ?? null)) {
          const current = await supabase.auth.getSession();
          if (disposed) return;
          verifiedTrust = !current.error && current.data.session?.user.id === userId && sessionIdentity(current.data.session) === sessionId;
          if (!verifiedTrust) clearDeviceTrust();
        }
      } catch { clearDeviceTrust(); verifiedTrust = false; /* failed check never extends device trust */ }
      finally { verifying = false; }
    };
    const onVisibility = async () => {
      if (document.visibilityState === "hidden") {
        hiddenAt.current = Date.now();
        return;
      }
      const away = hiddenAt.current ? Date.now() - hiddenAt.current : 0;
      hiddenAt.current = null;
      check();
      if (ending.current || away < BACKGROUND_RECHECK_MS) return;
      await verify();
    };

    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    const serverTimer = window.setInterval(() => void verify(), BACKGROUND_RECHECK_MS);
    if (trustedUntil(userId, sessionId ?? null)) void verify();
    return () => {
      disposed = true;
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(timer);
      window.clearInterval(serverTimer);
    };
  }, [userId, lastSignInAt, loginPath, navigate, sessionId]);

  return null;
}
